import "./static";
import MandelbrotMap from "./MandelbrotMap";
import { initApp } from "./app";
import { initAuthUI } from "./auth/authUI";
import { initBuyModal } from "./buyModal";
import { initCollection } from "./collection";
import { initClaimModal } from "./claimModal";
import { initGallery } from "./gallery";
import { initMerchPreview } from "./merchPreview";
import { store, actions, selectors } from "./store";
import { supabase } from "./api";
import { getParcels, getOrders } from "./db";
import { throttle } from "lodash";
import { trackEvent } from "./analytics";

const mapHtmlId = "leaflet";
const smallScreenWidthPx = 800;

// Global map instance - use getter to avoid circular dependency issues
let mapInstance: MandelbrotMap | null = null;
export function getMapInstance(): MandelbrotMap | null {
  return mapInstance;
}

window.addEventListener("load", () => {
  // Prevent FOUC - show body after styles loaded
  document.body.classList.add("loaded");

  // Service worker: register in production, unregister in development
  if ("serviceWorker" in navigator) {
    if (process.env.NODE_ENV === "production") {
      navigator.serviceWorker
        .register("/service-worker.js")
        .catch((err: unknown) => {
          console.error("Service worker registration failed:", err);
        });
    } else {
      // Unregister any existing service workers in development
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
      });
    }
  }

  // Restore fractal position from sessionStorage if URL params are missing.
  // This handles the case where PKCE OAuth drops query params from redirectTo.
  restorePositionFromSession();

  if (document.getElementById(mapHtmlId)) {
    const initialZoom = window.innerWidth <= smallScreenWidthPx ? 2 : 3;

    mapInstance = new MandelbrotMap({
      htmlId: mapHtmlId,
      initialConfig: {
        iterations: 200,
        exponent: 2,
        colorScheme: "turbo",
        lightenAmount: 0,
        saturateAmount: 0,
        shiftHueAmount: 0,
        colorSpace: 2,
        reverseColors: false,
        smoothColoring: true,
        paletteMinIter: 0,
        paletteMaxIter: 200,
        scaleWithIterations: true,

        re: -0.5,
        im: 0,
        zoom: initialZoom,
      },
    });

    // Sync map position to store (throttled to avoid excessive updates)
    setupMapStoreSync(mapInstance);

    // Initialize Fractalot modules with error boundaries
    try {
      initApp();
      console.log("[Fractalot] initApp complete");
    } catch (e) {
      console.error("[Fractalot] initApp failed:", e);
    }

    try {
      initAuthUI();
      console.log("[Fractalot] initAuthUI complete");
    } catch (e) {
      console.error("[Fractalot] initAuthUI failed:", e);
    }

    try {
      initBuyModal();
      console.log("[Fractalot] initBuyModal complete");
    } catch (e) {
      console.error("[Fractalot] initBuyModal failed:", e);
    }

    try {
      initCollection();
      console.log("[Fractalot] initCollection complete");
    } catch (e) {
      console.error("[Fractalot] initCollection failed:", e);
    }

    try {
      initClaimModal();
      console.log("[Fractalot] initClaimModal complete");
    } catch (e) {
      console.error("[Fractalot] initClaimModal failed:", e);
    }

    try {
      initGallery();
      console.log("[Fractalot] initGallery complete");
    } catch (e) {
      console.error("[Fractalot] initGallery failed:", e);
    }

    try {
      initMerchPreview();
      console.log("[Fractalot] initMerchPreview complete");
    } catch (e) {
      console.error("[Fractalot] initMerchPreview failed:", e);
    }

    // Handle return from OAuth (PKCE code exchange) and Stripe
    handleReturnParams();

    // Track page view
    trackEvent("page_view");
  }
});

/**
 * Restore fractal position from sessionStorage into URL params.
 * Called synchronously before map init so setConfigFromUrl() picks them up.
 * Only applies if the URL doesn't already have position params (re, im, z).
 */
function restorePositionFromSession(): void {
  const params = new URLSearchParams(window.location.search);
  // If URL already has position params (from redirectTo), skip
  if (params.has("re") && params.has("im") && params.has("z")) return;

  try {
    const saved = sessionStorage.getItem("fractalot_pre_login_pos");
    if (!saved) return;

    // Clean up immediately so it only applies once
    sessionStorage.removeItem("fractalot_pre_login_pos");

    const posData = JSON.parse(saved) as Record<string, string>;
    if (!posData.re || !posData.im || !posData.z) return;

    // Inject position params into the current URL so setConfigFromUrl() reads them
    const url = new URL(window.location.href);
    for (const [key, val] of Object.entries(posData)) {
      url.searchParams.set(key, val);
    }
    window.history.replaceState({}, "", url.toString());
    console.log("[Auth] Restored position from sessionStorage");
  } catch (_) { /* parse error or storage unavailable */ }
}

/**
 * Handle URL parameters from Stripe redirect
 */
async function handleReturnParams(): Promise<void> {
  const params = new URLSearchParams(window.location.search);

  // PKCE OAuth: exchange auth code for session
  const code = params.get("code");
  if (code && supabase) {
    console.log("[Auth] PKCE code detected, exchanging for session");
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) {
      console.error("[Auth] PKCE code exchange failed:", error.message);
    }
    // Clean up the code param (position params are cleaned by setConfigFromUrl)
    const url = new URL(window.location.href);
    url.searchParams.delete("code");
    window.history.replaceState({}, "", url.toString());
  }

  if (params.get("claim_success") === "true") {
    const parcelId = params.get("parcel_id");

    // Clean up URL params
    const url = new URL(window.location.href);
    url.searchParams.delete("claim_success");
    url.searchParams.delete("parcel_id");
    window.history.replaceState({}, "", url.toString());

    // Refresh parcel data to get updated claim status
    const user = store.getState().user;
    if (user) {
      const parcels = await getParcels(user.id);
      actions.setParcels(parcels);
    }

    // Show success toast
    showToast("Coordinate claimed! It's now in the public gallery.");
    trackEvent("claim_success", { parcelId: parcelId || "" });
  }

  if (params.get("claim_cancelled") === "true") {
    const url = new URL(window.location.href);
    url.searchParams.delete("claim_cancelled");
    window.history.replaceState({}, "", url.toString());
  }

  if (params.get("merch_success") === "true") {
    const product = params.get("product") || "item";

    const url = new URL(window.location.href);
    url.searchParams.delete("merch_success");
    url.searchParams.delete("product");
    window.history.replaceState({}, "", url.toString());

    // Refresh orders to show the new order in collection
    const user = store.getState().user;
    if (user) {
      getOrders().then((orders) => actions.setOrders(orders));
    }

    const names: Record<string, string> = {
      tshirt: "T-Shirt",
      sweatshirt: "Sweatshirt",
      mug: "Mug",
    };
    const label = names[product] || product;
    showToast(`${label} order placed! You'll receive a confirmation email.`);
    trackEvent("merch_success", { product });
  }

  if (params.get("merch_cancelled") === "true") {
    const url = new URL(window.location.href);
    url.searchParams.delete("merch_cancelled");
    window.history.replaceState({}, "", url.toString());
  }
}

/**
 * Show a toast notification
 */
function showToast(message: string): void {
  const toast = document.createElement("div");
  toast.className = "purchase-toast";
  toast.textContent = message;
  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.add("visible");
  });

  setTimeout(() => {
    toast.classList.remove("visible");
    setTimeout(() => toast.remove(), 300);
  }, 4000);
}

/**
 * Two-way sync between MandelbrotMap and store
 */
function setupMapStoreSync(map: MandelbrotMap): void {
  let updatingFromStore = false;

  const syncMapToStore = throttle(() => {
    if (updatingFromStore) return;
    if (!map.config) return;

    const { re, im, zoom } = map.config;
    const current = store.getState().mapPosition;

    if (re !== current.re || im !== current.im || zoom !== current.zoom) {
      actions.setMapPosition({ re, im, zoom });
    }
  }, 200);

  map.on("moveend zoomend", syncMapToStore);

  store.select(selectors.mapPosition, (position) => {
    if (!position || !map.config) {
      return;
    }

    const { re, im, zoom } = map.config;

    if (position.re !== re || position.im !== im || position.zoom !== zoom) {
      updatingFromStore = true;

      const url = new URL(window.location.href);
      url.searchParams.set("re", position.re.toString());
      url.searchParams.set("im", position.im.toString());
      url.searchParams.set("z", position.zoom.toString());
      window.history.replaceState({}, "", url.toString());

      map.setConfigFromUrl();

      setTimeout(() => {
        updatingFromStore = false;
      }, 300);
    }
  });

  syncMapToStore();
}
